import { LocalizationService } from '@abp/ng.core';
import { ZendraLocalizationServices } from '../enums/abpLocalizationServices';

const DEFAULT_FALLBACK_RESOURCES: ZendraLocalizationServices[] = [
  ZendraLocalizationServices.General,
  ZendraLocalizationServices.Controlling,
  ZendraLocalizationServices.Planning,
  ZendraLocalizationServices.WoodSecurity,
  ZendraLocalizationServices.WoodSupply,
  ZendraLocalizationServices.LanguageService,
  ZendraLocalizationServices.Geographic,
  ZendraLocalizationServices.Planning,
  ZendraLocalizationServices.Operational
];

function instantLabel(
  localizationService: LocalizationService | null | undefined,
  key: string,
  interpolateArgs: unknown[] = []
): string {
  if (!localizationService) return key;
  const instant = localizationService.instant?.bind(localizationService);
  if (typeof instant !== 'function') return key;

  return instant(key, ...(interpolateArgs as any[]));
}

function isFallbackResources(
  value: unknown
): value is ZendraLocalizationServices[] {
  if (!Array.isArray(value)) return false;
  return value.every(resource =>
    Object.values(ZendraLocalizationServices).includes(
      resource as ZendraLocalizationServices
    )
  );
}

export function localizeWithFallback(
  localizationService: LocalizationService | null | undefined,
  key: string,
  fallbackResourcesOrArg: ZendraLocalizationServices[] | unknown = DEFAULT_FALLBACK_RESOURCES,
  ...interpolateArgs: unknown[]
): string {
  if (!key) return '';

  const fallbackResources = isFallbackResources(fallbackResourcesOrArg)
    ? fallbackResourcesOrArg
    : DEFAULT_FALLBACK_RESOURCES;

  const instantArgs = isFallbackResources(fallbackResourcesOrArg)
    ? interpolateArgs
    : [fallbackResourcesOrArg, ...interpolateArgs].filter(v => v !== undefined);

  if (key.includes('::')) {
    const label = instantLabel(localizationService, key, instantArgs);
    const raw = key.split('::').slice(1).join('::');
    if (label !== key && label !== raw) {
      return label;
    }
    return localizeWithFallback(
      localizationService,
      raw,
      fallbackResources,
      ...instantArgs
    );
  }

  for (const resource of fallbackResources) {
    const candidateKey = `${resource}::${key}`;
    const label = instantLabel(localizationService, candidateKey, instantArgs);
    if (label !== candidateKey && label !== key) {
      return label;
    }
  }

  return key;
}

export function createLocalizationKey(name: string, nameSpace: ZendraLocalizationServices): string {
  return `${nameSpace}::${name}`
}
